import { jsx, jsxs, Fragment } from 'react/jsx-runtime';
import { Flex } from '@fabric-app/ui/components/Flex';
import { P, SPAN } from '@fabric-app/ui/components/Typography';
import { ColorLabel } from '../../ResourcePreview/components/ColorLabel.js';
import { styled } from 'styled-components';
import Skeleton$1 from '@fabric-app/ui/components/Skeleton';
import { getDisplayKind } from '../../../utils/getDisplayKind.js';
import { getResourceNamePlaceholder } from '../../../utils/getResourceTitle.js';
import AvatarUser from '../../../../user/components/AvatarUser/AvatarUser.js';
import StateSpinner, { useResourceLoadingStateIndicatorProps } from '../../StateSpinner.js';
import { ResourcePreviewListPreview } from '../../ResourcePreview/ResourcePreviewList/preview/ResourcePreviewListItemPreview.js';
import { formatFileSize } from '@fabric-app/utils/number';
import { ResourcePreviewTags } from '../../ResourcePreview/ResourcePreviewGrid/ResourcePreviewTags.js';
import { cssVar } from '@fabric-app/ui/theme/variables';
import { cssTextEllipsis, cssLineClamp } from '@fabric-app/ui/constants/styled-utils';
import { LinkExternal } from '@fabric-app/ui/components/Link/LinkExternal';
import { IconSharedWithPublic } from '../../IconSharedWithPublic.js';
import { dayTimeFormat } from '@fabric-app/compat/dayjs';
import { IconGlobe } from '@fabric-app/icons/svg';
import dayjs from 'dayjs';
import { isResourceStateProcessing } from '../../../utils/isResourceStateProcessing.js';
import { isResourceDetailBookmark, isResourceDetailEmail } from '../../../utils/resourceDetailGuards.js';

const Skeleton = styled(Skeleton$1).withConfig({
  displayName: "cellMap__Skeleton",
  componentId: "sc-a0c6867e-0"
})([
  `border-radius:4px;`
]);
const createResourceDataCell = (Cmp) => function BaseCell(props) {
  if ("kind" in props.data) {
    return /* @__PURE__ */ jsx(Cmp, {
      ...props,
      data: props.data
    });
  }
  return null;
};
const cellMap = {
  name: createResourceDataCell(({ data }) => {
    const isProcessing = isResourceStateProcessing(data.stateProcessing);
    const isProcessingName = isResourceDetailBookmark(data) && isProcessing && (data.name === null || data.name === "");
    return /* @__PURE__ */ jsxs(CellContent, {
      flexGrow: true,
      gap: "element",
      justifyContent: "space-between",
      children: [
        isResourceDetailEmail(data) ? /* @__PURE__ */ jsxs(P, {
          size: "xs",
          color: "tertiary",
          ellipsis: true,
          as: "div",
          children: [
            data.data.emailMessage?.from ? `${data.data.emailMessage.from} - ` : "",
            data.name || /* @__PURE__ */ jsx(SPAN, {
              color: "placeholder",
              children: "no subject"
            }),
            data.extension && /* @__PURE__ */ jsxs(SPAN, {
              color: "placeholder",
              children: [
                ".",
                data.extension
              ]
            })
          ]
        }) : isProcessingName ? /* @__PURE__ */ jsx(Skeleton, {
          height: 13,
          width: "200px",
          borderRadius: 6
        }) : /* @__PURE__ */ jsxs(P, {
          size: "xs",
          color: "tertiary",
          ellipsis: true,
          as: "div",
          children: [
            data.name || /* @__PURE__ */ jsx(P, {
              color: "placeholder",
              children: getResourceNamePlaceholder(data)
            }),
            data.extension && /* @__PURE__ */ jsxs(SPAN, {
              color: "placeholder",
              children: [
                ".",
                data.extension
              ]
            })
          ]
        }),
        /* @__PURE__ */ jsxs(Flex, {
          alignItems: "center",
          gap: "element",
          children: [
            data.label && /* @__PURE__ */ jsx(ColorLabel, {
              absPosition: "disabled"
            }),
            data.isDirectShared && /* @__PURE__ */ jsx(IconSharedWithPublic, {})
          ]
        })
      ]
    });
  }),
  createdAt: createResourceDataCell(({ data }) => /* @__PURE__ */ jsx(CellContent, {
    children: /* @__PURE__ */ jsx(EllipsizedContent, {
      children: dayjs(data.createdAt).format(dayTimeFormat)
    })
  })),
  modifiedAt: createResourceDataCell(({ data }) => /* @__PURE__ */ jsx(CellContent, {
    children: /* @__PURE__ */ jsx(EllipsizedContent, {
      children: dayjs(data.modifiedAt).format(dayTimeFormat)
    })
  })),
  comments: createResourceDataCell(({ data }) => /* @__PURE__ */ jsx(CellContent, {
    gap: "element",
    children: data.commentPinned && /* @__PURE__ */ jsxs(Fragment, {
      children: [
        /* @__PURE__ */ jsx(AvatarUser, {
          user: data.commentPinned.user,
          size: 16
        }),
        /* @__PURE__ */ jsx(CommentText, {
          children: data.commentPinned.content
        })
      ]
    })
  })),
  kind: createResourceDataCell(({ data }) => /* @__PURE__ */ jsx(CellContent, {
    children: /* @__PURE__ */ jsx(EllipsizedContent, {
      children: getDisplayKind(data)
    })
  })),
  preview: createResourceDataCell(({ data }) => {
    const { isIndexing, showIndicator } = useResourceLoadingStateIndicatorProps(data);
    return /* @__PURE__ */ jsx(CellContent, {
      justifyContent: "center",
      alignItems: "center",
      children: showIndicator ? /* @__PURE__ */ jsx(StateSpinner, {
        size: 22,
        isIndexing
      }) : /* @__PURE__ */ jsx(ResourcePreviewListPreview, {})
    });
  }),
  size: createResourceDataCell(({ data }) => /* @__PURE__ */ jsx(CellContent, {
    justifyContent: "flex-end",
    children: /* @__PURE__ */ jsx(EllipsizedContent, {
      children: data.size ? formatFileSize(data.size) : null
    })
  })),
  tags: createResourceDataCell(({ data }) => /* @__PURE__ */ jsx(CellContent, {
    children: /* @__PURE__ */ jsx(ResourcePreviewTags, {
      tags: data.tags
    })
  })),
  url: createResourceDataCell(({ data }) => {
    if (!data.originUrl) {
      return;
    }
    const displayedUrl = (data.originUrl.includes("//") ? data.originUrl.split("//")[1] : data.originUrl).replace("www.", "").replace(/\/$/, "");
    return /* @__PURE__ */ jsxs(CellContent, {
      gap: "element",
      alignItems: "center",
      children: [
        /* @__PURE__ */ jsx(StyledIconGlobe, {}),
        /* @__PURE__ */ jsx(LinkUrl, {
          href: data.originUrl,
          children: displayedUrl
        })
      ]
    });
  }),
  createdBy: createResourceDataCell(({ data }) => /* @__PURE__ */ jsx(CellContent, {
    children: /* @__PURE__ */ jsxs(Flex, {
      gap: "element",
      alignItems: "center",
      overflow: "hidden",
      children: [
        /* @__PURE__ */ jsx(AvatarUser, {
          size: 20,
          user: data.user
        }),
        /* @__PURE__ */ jsx(EllipsizedContent, {
          children: data.user?.name || "Unknown"
        })
      ]
    })
  }))
};
const cellSkeletonMap = {
  name: () => /* @__PURE__ */ jsx(CellContent, {
    children: /* @__PURE__ */ jsx(Skeleton, {
      height: 13,
      width: 200
    })
  }),
  createdAt: () => /* @__PURE__ */ jsx(CellContent, {
    children: /* @__PURE__ */ jsx(Skeleton, {
      height: 13,
      width: 100
    })
  }),
  modifiedAt: () => /* @__PURE__ */ jsx(CellContent, {
    children: /* @__PURE__ */ jsx(Skeleton, {
      height: 13,
      width: 100
    })
  }),
  comments: () => /* @__PURE__ */ jsx(CellContent, {
    children: /* @__PURE__ */ jsx(Skeleton, {
      height: 13,
      width: 140
    })
  }),
  kind: () => /* @__PURE__ */ jsx(CellContent, {
    children: /* @__PURE__ */ jsx(Skeleton, {
      height: 13,
      width: 80
    })
  }),
  preview: () => /* @__PURE__ */ jsxs(CellContent, {
    justifyContent: "center",
    children: [
      " ",
      /* @__PURE__ */ jsx(Skeleton, {
        width: 24,
        height: 24
      })
    ]
  }),
  size: () => /* @__PURE__ */ jsx(CellContent, {
    children: /* @__PURE__ */ jsx(Skeleton, {
      height: 13,
      width: 80
    })
  }),
  tags: () => /* @__PURE__ */ jsx(CellContent, {
    children: /* @__PURE__ */ jsx(Skeleton, {
      height: 16,
      width: 80
    })
  }),
  url: () => /* @__PURE__ */ jsx(CellContent, {
    children: /* @__PURE__ */ jsx(Skeleton, {
      height: 13,
      width: 170
    })
  }),
  createdBy: () => /* @__PURE__ */ jsx(CellContent, {
    children: /* @__PURE__ */ jsx(Skeleton, {
      height: 13,
      width: 220
    })
  })
};
const CellContent = styled(Flex).attrs((p) => {
  return {
    alignItems: "center",
    ...p
  };
}).withConfig({
  displayName: "cellMap__CellContent",
  componentId: "sc-a0c6867e-1"
})([
  `height:40px;padding:0 10px;color:`,
  `;font-size:13px;position:relative;[data-tag-container-inner]{justify-content:flex-start;}`
], cssVar["color-text-tertiary"]);
const LinkUrl = styled(LinkExternal).withConfig({
  displayName: "cellMap__LinkUrl",
  componentId: "sc-a0c6867e-2"
})([
  ``,
  ` &:hover{color:`,
  `;}`
], cssTextEllipsis, cssVar["color-app-primary"]);
const StyledIconGlobe = styled(IconGlobe).withConfig({
  displayName: "cellMap__StyledIconGlobe",
  componentId: "sc-a0c6867e-3"
})([
  `height:1rem;width:1rem;`
]);
const CommentText = styled.div.withConfig({
  displayName: "cellMap__CommentText",
  componentId: "sc-a0c6867e-4"
})([
  `white-space:nowrap;text-overflow:ellipsis;overflow:hidden;color:`,
  `;background:`,
  `;border-radius:0.25rem;word-break:break-word;hyphens:auto;`,
  ` padding:2px 3px;line-height:1.25;font-size:0.75rem;font-weight:500;`
], cssVar["color-text-quaternary"], cssVar["color-bg-tertiary"], cssLineClamp(2));
const EllipsizedContent = styled.span.withConfig({
  displayName: "cellMap__EllipsizedContent",
  componentId: "sc-a0c6867e-5"
})([
  ``,
  ``
], cssTextEllipsis);

export { CellContent, cellMap, cellSkeletonMap };
//# sourceMappingURL=cellMap.js.map
